/**
 * useExternalWrites — client view of the external-write sandbox leases.
 *
 * State lives in a zustand store so the toolbar chip, popover and banner
 * share one snapshot. Exactly one consumer should pass `sync: true` (the
 * banner, mounted on every authenticated route); it owns the fetch-on-mount,
 * focus refetch and the single expiry timer.
 */

import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { useApi } from '../../lib/api';
import { ApiProblemError, WriteRefusedError, fetchJson } from '../../lib/apiFetchJson';
import { isProduction } from '../../lib/environment';
import { ExternalWritesStateSchema } from '@shared/external-writes/wire';
import type { ExternalWritesFlip, ExternalWritesState } from '@shared/external-writes/wire';
import type { ExternalSystem } from '@shared/external-writes/systems';
import { useExternalWritesStore } from './external-writes-store';

const ENDPOINT = '/api/admin/external-writes';
const EXPIRY_SKEW_MS = 2_000;
const MAX_TIMEOUT_MS = 2_147_483_647;


const FlipForbiddenBodySchema = z.object({
  code: z.enum([
    'external_writes_flip_forbidden_role',
    'external_writes_flip_forbidden_impersonating',
    'external_writes_flip_forbidden_api_key',
  ]),
});

const FORBIDDEN_MESSAGES = {
  external_writes_flip_forbidden_role: 'You need impersonation_writer',
  external_writes_flip_forbidden_impersonating: 'Stop impersonating first',
  external_writes_flip_forbidden_api_key: 'API keys cannot flip',
} as const;

function soonestLiveExpiryMs(state: ExternalWritesState | 'unknown', nowMs: number): number | null {
  if (state === 'unknown') return null;
  let soonest: number | null = null;
  for (const lease of state.systems) {
    if (lease.mode !== 'live') continue;
    const at = Date.parse(lease.liveUntil);
    if (at <= nowMs) continue;
    if (soonest === null || at < soonest) soonest = at;
  }
  return soonest;
}

export interface UseExternalWrites {
  state: ExternalWritesState | 'unknown';
  lastError: string | null;
  refresh: () => Promise<void>;
  lease: (systems: ExternalSystem[], minutes: number, reason: string) => Promise<void>;
  revoke: (systems: ExternalSystem[], reason: string) => Promise<void>;
}

export function useExternalWrites({ sync = true }: { sync?: boolean } = {}): UseExternalWrites {
  const { apiFetch } = useApi();
  const state = useExternalWritesStore((s) => s.state);
  const lastError = useExternalWritesStore((s) => s.lastError);

  // apiFetch identity churns with Clerk's getToken; keep it in a ref.
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;

  const refresh = useCallback(async () => {
    if (isProduction()) return;
    try {
      const next = await fetchJson(apiFetchRef.current, ExternalWritesStateSchema, ENDPOINT);
      useExternalWritesStore.setState({ state: next, lastError: null });
    } catch (err) {
      if (err instanceof ApiProblemError && err.kind === 'malformed') {
        useExternalWritesStore.setState({ state: 'unknown', lastError: null });
        return;
      }
      useExternalWritesStore.setState({
        state: 'unknown',
        lastError:
          err instanceof ApiProblemError && err.kind === 'http'
            ? `GET failed (${err.status})`
            : err instanceof Error
              ? err.message
              : 'fetch failed',
      });
    }
  }, []);

  const flip = useCallback(async (payload: ExternalWritesFlip) => {
    if (isProduction()) return;
    try {
      // The POST response is the post-flip state; no follow-up GET needed.
      const next = await fetchJson(apiFetchRef.current, ExternalWritesStateSchema, ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      useExternalWritesStore.setState({ state: next, lastError: null });
    } catch (err) {
      if (!(err instanceof ApiProblemError)) throw err;
      if (err.kind === 'malformed') {
        useExternalWritesStore.setState({ state: 'unknown', lastError: null });
        return;
      }
      if (err.kind !== 'http') throw err;
      // apiFetch already announced a refusal; only record it.
      if (err instanceof WriteRefusedError) {
        useExternalWritesStore.setState({ lastError: err.userMessage });
        return;
      }
      const forbidden = FlipForbiddenBodySchema.safeParse(err.body);
      const message = forbidden.success
        ? FORBIDDEN_MESSAGES[forbidden.data.code]
        : `Could not change external writes (${err.status})`;
      useExternalWritesStore.setState({ lastError: message });
      toast.error(message);
    }
  }, []);

  const lease = useCallback(
    async (systems: ExternalSystem[], minutes: number, reason: string) => {
      const [first, ...rest] = systems;
      if (first === undefined) return;
      await flip({ mode: 'live', systems: [first, ...rest], minutes, reason });
    },
    [flip],
  );

  const revoke = useCallback(
    async (systems: ExternalSystem[], reason: string) => {
      const [first, ...rest] = systems;
      if (first === undefined) return;
      await flip({ mode: 'sandbox', systems: [first, ...rest], reason });
    },
    [flip],
  );

  // Fetch on mount + on window focus.
  useEffect(() => {
    if (!sync || isProduction()) return;
    void refresh();
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [sync, refresh]);

  // One timer at the soonest liveUntil so the banner clears without a reload.
  useEffect(() => {
    if (!sync || isProduction()) return;
    const soonest = soonestLiveExpiryMs(state, Date.now());
    if (soonest === null) return;
    const delay = Math.min(Math.max(soonest - Date.now() + EXPIRY_SKEW_MS, 0), MAX_TIMEOUT_MS);
    const timer = setTimeout(() => {
      void refresh();
    }, delay);
    return () => clearTimeout(timer);
  }, [sync, state, refresh]);

  return { state, lastError, refresh, lease, revoke };
}
